import { Component, ElementRef, ViewChild, Input, SimpleChanges, OnChanges, SimpleChange } from '@angular/core';
import * as d3 from 'd3';

import { ChartData } from 'src/app/beans/dashboard/dashboard-module.bean';
import { Location } from '@angular/common';

@Component({
	selector: 'app-progress-bar',
	templateUrl: './progress-bar.component.html',
	styleUrls: ['./progress-bar.component.scss'],
})
export class ProgressBarComponent implements OnChanges {
	x;
	canvas;
	color: d3.ScaleOrdinal<number, string>;
	sets;
	colorScale;

	@ViewChild('svgContainer', { static: true })
	svgContainer: ElementRef;

	@Input() chartData: any;
	constructor(public location: Location) { }

	ngOnChanges(changes: SimpleChanges): void {
		for (const propName in changes) {
			if (Object.prototype.hasOwnProperty.call(changes, propName) && propName === 'chartData') {
				const change: SimpleChange = changes[propName];
				const cur: ChartData = change.currentValue;
				this.progressBuilder(cur);
			}
		}
	}

	progressBuilder(cur: ChartData) {
		const width = this.svgContainer.nativeElement.offsetWidth;
		let height = this.svgContainer.nativeElement.offsetWidth;

		if (height > 230) {
			height = 230;
		}
		const { attributeScale }: { attributeScale: number[][] } = cur;
		const { chartType }: { chartType: string } = cur;
		const { attributeValue }: { attributeValue: string } = cur;
		const { chartName }: { chartName: string } = cur;
		const { unitOfMeasurement }: { unitOfMeasurement: string } = cur;
		let maxDomain: number;
		if (chartType === 'calculated') {
			const domainCheck: number | null | undefined = attributeScale[attributeScale.length - 1][1];
			if (domainCheck === null || domainCheck === undefined) {
				const maxValues = {
					'BMI': 160,
					'Total Cholesterol': 400,
					'Hemoglobin A1c': 10,
					'Exercise Status': 2000,
					'Happiness Scale': 7,
					'Waist Circumference': 80,
					'LDL': 350,
					'HDL': 150,
					'Triglycerides': 500,
				};
				maxDomain = maxValues[chartName];
				attributeScale[attributeScale.length - 1][1] = maxDomain;
			} else {
				maxDomain = domainCheck;
			}
		} else {
			maxDomain = 0;
		}
		const { chartDisplay }: { chartDisplay: string } = cur;
		const { pointerColor }: { pointerColor: string } = cur;
		const { attributeDescription }: { attributeDescription: string } = cur;
		this.color = this.colorChanger(cur.colors);
		this.colorScale = cur.colors;
		this.drawCharts(
			attributeScale,
			attributeValue,
			maxDomain,
			chartType,
			chartName,
			unitOfMeasurement,
			attributeDescription,
			pointerColor,
			chartDisplay,
			width,
			height
		);
	}

	/**
	 *
	 * @description sets a color based on the attribute
	 * @param {string[]} colorScale is one of AttributeScalesColors
	 * @memberof WellnessPanelComponent
	 */
	colorChanger(colorScale: string[]): d3.ScaleOrdinal<number, string> {
		const colorDomain: number[] = colorScale.map((x, i) => i);
		return d3.scaleOrdinal<number, string>().range(colorScale).domain(colorDomain);
	}

	drawCharts(
		attributeScale: number[][],
		attributeValue: string,
		maxDomain: number,
		chartType: string,
		chartName: string,
		unitOfMeasurement: string,
		attributeDescription: string,
		pointerColor: string,
		chartDisplay: string,
		width,
		height
	) {
		this.x = this.xScaleChanger(chartType, maxDomain, attributeScale, width);
		this.canvas = this.createCanvas(chartName, chartDisplay, width, height);
		this.sets = this.loadScale(height);
		this.drawProgressLabel(attributeDescription, pointerColor, width, height);
		this.drawProgressValue(attributeValue, unitOfMeasurement, pointerColor, width, height);
		this.drawRectangles(chartType, attributeScale, width, maxDomain, chartName, height);
		this.drawIndicator(attributeValue, pointerColor, chartDisplay, attributeScale, width, height);
	}

	/**
	 *
	 * @description This creates base canvas for drawing our SVG
	 * @memberof WellnessPanelComponent
	 */
	createCanvas(
		chartName: string,
		chartDisplay: string,
		width,
		height
	): d3.Selection<SVGGElement, any, HTMLElement, any> {
		if (chartDisplay === 'multiple') {
			let selection;
			switch (chartName) {
				case 'Waist Circumference':
					selection = '.Waist-Circumference';
					break;
				case 'Blood Pressure':
					selection = '.Blood-Pressure';
					break;
				case 'Total Cholesterol':
					selection = '.Total-Cholesterol';
					break;
				case 'LDL':
					selection = '.LDL';
					break;
				case 'HDL':
					selection = '.HDL';
					break;
				case 'Triglycerides':
					selection = '.Triglycerides';
					break;
				case 'Hemoglobin-A1C':
					selection = '.Hemoglobin-A1C';
					break;
				case 'Exercise Status':
					selection = '.Exercise-Status';
					break;
				case 'Happiness Scale':
					selection = '.Happiness-Scale';
					break;
				case 'Alcohol':
					selection = '.Alcohol';
					break;
				case 'BMI':
					selection = '.BMI';
					break;
				default:
					break;
			}
			selection = `${selection} .c-progress .progress-chart`;
			return d3
				.select(selection)
				.append('svg')
				.attr('viewBox', `-20 0 ${width * 1.2} ${height}`)
				.attr('preserveAspectRatio', 'xMidYMid meet')
				.append('g');
		} else {
			d3.selectAll('.c-svg-container.progress-chart > *').remove();
			return d3
				.select('.c-svg-container.progress-chart')
				.append('svg')
				.attr('viewBox', `-12 0 ${width + 24} ${height}`)
				.attr('preserveAspectRatio', 'xMidYMid meet')
				.append('g');
		}
	}

	/**
	 *
	 * @description Sets a scale to normalize the data
	 * @param {number} maxDomain
	 * @memberof WellnessPanelComponent
	 */
	xScaleChanger(
		chartType: string,
		maxDomain: number,
		attributeScale: number[][],
		width
	): d3.ScaleLinear<number, number> | d3.ScaleBand<string> {
		if (chartType === 'calculated') {
			return d3.scaleLinear().domain([attributeScale[0][0], maxDomain]).range([0, width]);
		} else if (chartType === 'categorical') {
			const attributeDomain = attributeScale.map((element, i) => i.toString());
			return d3.scaleBand().domain(attributeDomain).range([0, width]).round(true).paddingInner(0.1);
		}
	}

	centerTranslation(height): string {
		let AdjustedHeight: number;
		if (height === 230) {
			AdjustedHeight = height * 0.75;
		} else {
			AdjustedHeight = height * 0.45;
		}
		return `translate(0, ${AdjustedHeight})`;
	}

	drawProgressLabel(attributeDescription: string, pointerColor: string, width, height) {
		let labelSize = width / 8;
		// to dynamically resize the text to fit within the
		// viewBox of the svg and to respond to greater
		// ranges of text
		if (attributeDescription.length > 17) {
			labelSize = labelSize / 1.5;
		} else if (attributeDescription.length > 25) {
			labelSize = labelSize / 1.7;
		}
		let translateY: number;
		if (height === 230) {
			translateY = height * 0.225;
		} else {
			translateY = height * 0.15;
		}
		this.canvas
			.append('g')
			.attr('class', 'gaugeLabel')
			.attr('transform', `translate(${width / 2},${translateY})`)
			.append('text')
			.text(`${attributeDescription}`)
			.attr('fill', pointerColor)
			.style('text-anchor', 'middle')
			.style('font-size', `${labelSize}px`)
			.style('font-weight', 'bold')
			.style('margin-bottom', '10px');
	}

	drawProgressValue(patientAttribute: string, unitOfMeasurement: string, pointerColor: string, width, height) {
		let displayValue = patientAttribute;
		if (unitOfMeasurement != null) {
			displayValue = `${displayValue} ${unitOfMeasurement}`;
		}
		let valueSize = width / 10;
		let translateY: number;
		if (height === 230) {
			translateY = height * 0.45;
		} else if (width <= 78) {
			translateY = (height * 0.25) - (width * 0.15);
			valueSize = width / 8;
		} else {
			translateY = (height * 0.25) - (width * 0.04);
		}
		this.canvas
			.append('g')
			.attr('class', 'gaugeValue')
			.attr('transform', `translate(${width / 2},${translateY})`)
			.append('text')
			.attr('y', 18)
			.text(displayValue)
			.attr('fill', pointerColor)
			.style('text-anchor', 'middle')
			.style('font-size', `${valueSize}px`)
			.style('font-weight', 'normal');
	}

	/**
	 *
	 * @description Sets up the data for the needed attribute
	 * @param {(number[][] | BloodPressure[])} scale is one of AttributeScales
	 * @memberof WellnessPanelComponent
	 */
	loadScale(height): any {
		return this.canvas.append('g').attr('transform', this.centerTranslation(height));
	}

	/**
	 *
	 * @description Draws each of the chart data elements
	 * @memberof WellnessPanelComponent
	 */
	drawRectangles(chartType: string, attributeScale: number[][], width, maxDomain: number, chartName: string, height) {
		const noSpaceName = chartName.replace(' ', '');
		const colorDomain: number[] = this.colorScale.map((x, i) => i);
		const colorDomainSize = colorDomain.length;
		const gradientSelector = `${noSpaceName}-gradient`;
		const gradientUrl = `url(${this.location.path()}#${gradientSelector})`;
		const linearGradient = this.canvas.append('defs').append('linearGradient').attr('id', `${gradientSelector}`);
		let barsHeight: string;
		if (height === 230) {
			barsHeight = '13%';
		} else {
			barsHeight = '10%';
		}

		if (chartType === 'calculated') {
			const percentage = d3.scaleLinear().domain([attributeScale[0][0], maxDomain]).range([0, 100]);
			let offsetSize = 0;

			colorDomain.forEach(i => {
				const maxVal = attributeScale[i][1];
				offsetSize = percentage(maxVal);
				linearGradient.append('stop').attr('offset', `${offsetSize}%`).attr('stop-color', this.color(i));
			});
			this.sets
				.append('rect')
				.attr('x', 0)
				.attr('width', width)
				.attr('height', barsHeight)
				.attr('fill', `${gradientUrl}`);
		} else if (chartType === 'categorical') {
			const offsetChange = 100 / (colorDomainSize - 1);
			let offsetSize = 0;

			colorDomain.forEach(i => {
				linearGradient.append('stop').attr('offset', `${offsetSize}%`).attr('stop-color', this.color(i));
				offsetSize += offsetChange;
			});
			this.sets
				.append('rect')
				.attr('x', 0)
				.attr('width', width)
				.attr('height', barsHeight)
				.attr('fill', `${gradientUrl}`);
		}
	}

	/**
	 *
	 * @description Draws a line and Triangle of where the patient data is
	 * @memberof WellnessPanelComponent
	 */
	drawIndicator(
		patientAttribute: string,
		pointerColor: string,
		chartDisplay: string,
		attributeScale: number[][],
		width,
		height
	) {
		const transitionMs = 500;
		let point = 0;
		if (patientAttribute.split('/').length < 2) {
			point = this.x(patientAttribute);
		} else {
			const spacing = width / (attributeScale.length * 2);
			const pointerLocation = this.colorScale.indexOf(pointerColor);
			const pointerMultiplier = pointerLocation * 2 + 1;
			point = spacing * pointerMultiplier;
		}
		if (point < 0) {
			point = this.x(attributeScale[0][0]);
		}
		const numberArea = this.canvas.append('g').attr('transform', this.centerTranslation(height));
		let lineSize = '10%';
		point += 1;
		let symbolSize;
		const pointerLine = d3.symbolTriangle;
		let triangleDistance;
		if (height === 230) {
			triangleDistance = 43;
			symbolSize = 250;
			lineSize = '13%';
		} else if (chartDisplay === 'multiple') {
			triangleDistance = 25;
			symbolSize = 100;
		} else {
			triangleDistance = 30;
			symbolSize = 150;
		}
		// Drawing a line that is where the Patient Data
		const line = numberArea
			.append('rect')
			.attr('width', 2)
			.attr('height', lineSize)
			.attr('x', 0)
			.attr('fill', 'black')
			.transition()
			.duration(transitionMs)
			.attr('x', point - 2);
		/**
		 * Here we are drawing A triangle below the line we just generated
		 * Point is increased by 1 to being in the middle of the line we just drew
		 */
		const symbol = d3.symbol().size(symbolSize);
		numberArea
			.append('path')
			.attr('d', symbol.type(pointerLine))
			.attr('fill', pointerColor)
			.transition()
			.duration(transitionMs)
			.attrTween('transform', interpolate => {
				return d3.interpolate(
					'translate(0,' + triangleDistance + ')',
					'translate(' + (point - 2) + ',' + triangleDistance + ')'
				);
			});
	}
}
